import React, { useCallback, useRef, useState } from 'react';
import { SRT_MAX_DB, SRT_MIN_DB } from './speechLogic';

const RE_COLOR = '#dc2626';
const LE_COLOR = '#2563eb';

/**
 * SrtBarGraph — two-bar chart for Speech Reception Threshold.
 *
 * • X = two categorical columns (Right Ear · Left Ear)
 * • Y = Intensity dB HL, INVERTED (0 at top → 60 at bottom)
 * • Bar fills bottom-up from 60 dB to the SRT value; a numeric label
 *   sits just above the bar-top.
 * • Click / drag anywhere in the plot area at column X, height Y →
 *   writes SRT for that ear (snaps to nearest 5 dB).
 */
const SrtBarGraph = ({
  srt,             // { right: number|null, left: number|null }
  onSetValue,      // (ear, dbHL) => void
  readOnly = false,
  width = 460, height = 240,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const [dragging, setDragging] = useState(null);
  const marginL = 46, marginR = 12, marginT = 22, marginB = 40;
  const plotW = width - marginL - marginR;
  const plotH = height - marginT - marginB;
  const barW = plotW / 4;
  const cols = [
    { key: 'right', label: 'Right Ear (RE)', color: RE_COLOR },
    { key: 'left',  label: 'Left Ear (LE)',  color: LE_COLOR },
  ];
  const colX = (idx) => marginL + plotW * (idx + 0.5) / cols.length;
  const yScale = useCallback((db) => marginT + ((db - SRT_MIN_DB) * plotH) / (SRT_MAX_DB - SRT_MIN_DB), [plotH]);
  const yInv = useCallback((py) => {
    const raw = SRT_MIN_DB + ((py - marginT) * (SRT_MAX_DB - SRT_MIN_DB)) / plotH;
    return Math.max(SRT_MIN_DB, Math.min(SRT_MAX_DB, Math.round(raw / 5) * 5));
  }, [plotH]);

  const toViewBox = (evt) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    const p = pt.matrixTransform(svg.getScreenCTM().inverse());
    return { x: p.x, y: p.y };
  };

  const snap = (p) => {
    if (!p) return null;
    if (p.y < marginT - 4 || p.y > height - marginB + 4) return null;
    if (p.x < marginL - 6 || p.x > width - marginR + 6) return null;
    let bestIdx = 0, bestDx = Infinity;
    cols.forEach((_, i) => {
      const dx = Math.abs(colX(i) - p.x);
      if (dx < bestDx) { bestDx = dx; bestIdx = i; }
    });
    return { idx: bestIdx, ear: cols[bestIdx].key, db: yInv(Math.max(marginT, Math.min(height - marginB, p.y))) };
  };

  const handleMouseDown = (evt) => {
    if (readOnly) return;
    const s = snap(toViewBox(evt));
    if (!s) return;
    setDragging(s.ear);
    onSetValue && onSetValue(s.ear, s.db);
  };
  const handleMouseMove = (evt) => {
    const s = snap(toViewBox(evt));
    setHover(s);
    if (readOnly || !dragging) return;
    if (s && s.ear === dragging) onSetValue && onSetValue(dragging, s.db);
  };
  const stopDrag = () => setDragging(null);

  const yTicks = [0, 10, 20, 30, 40, 50, 60];

  return (
    <div className="w-full">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        width="100%" height={height}
        preserveAspectRatio="none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={() => { stopDrag(); setHover(null); }}
        data-testid="srt-graph"
        style={{ cursor: readOnly ? 'default' : 'ns-resize', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 4, display: 'block', userSelect: 'none' }}
      >
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={marginL} x2={width - marginR} y1={yScale(t)} y2={yScale(t)} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={marginL - 6} y={yScale(t) + 3} fontSize="9" fill="#64748b" textAnchor="end">{t}</text>
          </g>
        ))}
        <text x={marginL - 34} y={marginT + plotH / 2 - 10} fontSize="9" fill="#475569" transform={`rotate(-90, ${marginL - 34}, ${marginT + plotH / 2})`} textAnchor="middle">Intensity (dB HL)</text>
        {cols.map((c, i) => {
          const cx = colX(i);
          const val = srt?.[c.key];
          const shown = val !== null && val !== undefined && Number.isFinite(Number(val));
          const barTop = shown ? yScale(Number(val)) : yScale(0);
          const barBottom = yScale(SRT_MAX_DB);
          return (
            <g key={c.key} data-testid={`srt-bar-${c.key}`}>
              {shown && (
                <rect x={cx - barW * 0.30} y={barTop} width={barW * 0.60} height={Math.max(0, barBottom - barTop)} fill={c.color} rx="2" />
              )}
              {!shown && (
                <rect x={cx - barW * 0.30} y={barBottom - 2} width={barW * 0.60} height="2" fill="#cbd5e1" />
              )}
              {shown && (
                <>
                  <rect x={cx - 44} y={Math.max(marginT + 2, barTop - 22)} width="88" height="20" rx="4" fill="#ffffff" stroke={c.color} strokeWidth="1.25" />
                  <text x={cx} y={Math.max(marginT + 15, barTop - 7)} fontSize="12" fill={c.color} textAnchor="middle" fontWeight="800" data-testid={`srt-bar-label-${c.key}`}>{`${val} dB HL`}</text>
                </>
              )}
              <text x={cx} y={height - marginB + 16} fontSize="10" fill={c.color} textAnchor="middle" fontWeight="700">
                {c.label}
              </text>
            </g>
          );
        })}
        {!readOnly && hover && (
          <g style={{ pointerEvents: 'none' }} data-testid="srt-hover">
            <line x1={colX(hover.idx)} x2={colX(hover.idx)} y1={marginT} y2={height - marginB} stroke={cols[hover.idx].color} strokeWidth="0.6" strokeDasharray="2 2" opacity="0.5" />
            <rect x={colX(hover.idx) + 6} y={Math.max(marginT, yScale(hover.db) - 10)} width="54" height="14" rx="2" fill="#0f172a" opacity="0.85" />
            <text x={colX(hover.idx) + 8} y={Math.max(marginT, yScale(hover.db) - 10) + 10} fontSize="9" fill="#ffffff" fontWeight="600">
              {hover.db} dB HL
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default SrtBarGraph;
